import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, withModifiers as _withModifiers, toDisplayString as _toDisplayString, createVNode as _createVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "audio-player__container" }
const _hoisted_2 = ["disabled"]
const _hoisted_3 = { class: "audio-player__controls" }
const _hoisted_4 = { class: "audio-player__progress-wrapper" }
const _hoisted_5 = { class: "audio-player__progress-track" }
const _hoisted_6 = ["value", "disabled"]
const _hoisted_7 = { class: "audio-player__time" }
const _hoisted_8 = { class: "time-current" }
const _hoisted_9 = { class: "time-total" }
const _hoisted_10 = { class: "audio-player__volume" }
const _hoisted_11 = ["value"]
const _hoisted_12 = ["src"]
const _hoisted_13 = {
  key: 0,
  class: "audio-player__error"
}

import { Icon } from "@iconify/vue";
import { computed, onUnmounted, ref, watch } from "vue";
import { useStore } from "vuex";

interface Props {
  recordingSid?: string;
  recordingUrl?: string;
  duration?: number;
  compact?: boolean;
  autoplay?: boolean;
  showCloseButton?: boolean;
  hideLoadingState?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AudioPlayer',
  props: {
    recordingSid: { default: undefined },
    recordingUrl: { default: undefined },
    duration: { default: 0 },
    compact: { type: Boolean, default: false },
    autoplay: { type: Boolean, default: false },
    showCloseButton: { type: Boolean, default: false },
    hideLoadingState: { type: Boolean, default: false }
  },
  emits: ["play", "pause", "ended", "error", "close"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const store = useStore();

const audioUrl = computed(() => {
  const token = store.getters.sessionToken;
  if (token && props.recordingSid) {
    const base = `${
      process.env.NODE_ENV === "development"
        ? process.env.VUE_APP_LOCAL_SERVER_URL
        : process.env.VUE_APP_SERVER_URL
    }/${process.env.VUE_APP_VERSION}`;
    let apiPath = `/twilio/play-recording/${props.recordingSid}`;
    if (!base.includes("/api/v1") && !base.includes("/api/")) {
      apiPath = `/api/v1${apiPath}`;
    }
    const url = `${base}${apiPath}`;
    return `${url}?token=${encodeURIComponent(token)}`;
  }
  return props.recordingUrl || null;
});

const audioElement = ref<HTMLAudioElement | null>(null);
const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const isLoading = ref(false);
const hasError = ref(false);

const volume = ref(0.7);
const isMuted = ref(false);
const previousVolume = ref(0.7);

const progress = computed(() => {
  if (!duration.value || !isFinite(duration.value) || duration.value === 0) {
    return 0;
  }
  if (!isFinite(currentTime.value)) {
    return 0;
  }
  const calculated = (currentTime.value / duration.value) * 100;
  return Math.min(100, Math.max(0, calculated));
});

const formattedCurrentTime = computed(() => {
  const time = currentTime.value;
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
});

const formattedAudioDuration = computed(() => {
  let durationToUse = duration.value;

  if (!durationToUse || !isFinite(durationToUse) || durationToUse === 0) {
    durationToUse = props.duration || 0;
  }

  if (!durationToUse || !isFinite(durationToUse)) {
    return "0:00";
  }

  const minutes = Math.floor(durationToUse / 60);
  const seconds = Math.floor(durationToUse % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
});

const volumePercent = computed(() => {
  return Math.round(volume.value * 100);
});

const volumeIcon = computed(() => {
  if (isMuted.value || volume.value === 0) {
    return "mdi:volume-off";
  } else if (volume.value < 0.3) {
    return "mdi:volume-low";
  } else if (volume.value < 0.7) {
    return "mdi:volume-medium";
  } else {
    return "mdi:volume-high";
  }
});

const togglePlayback = async () => {
  if (!audioElement.value) return;

  try {
    if (isPlaying.value) {
      audioElement.value.pause();
      isPlaying.value = false;
      emit("pause");
    } else {
      await audioElement.value.play();
      isPlaying.value = true;
      emit("play");
    }
  } catch (error) {
    console.error("Error playing audio:", error);
    hasError.value = true;
    isPlaying.value = false;
    emit("error", error instanceof Error ? error : new Error("Unknown error"));
  }
};

const seekTo = (event: Event) => {
  if (!audioElement.value || duration.value === 0) return;
  const target = event.target as HTMLInputElement;
  const seekTime = (parseFloat(target.value) / 100) * duration.value;
  audioElement.value.currentTime = seekTime;
  currentTime.value = seekTime;
};

const onLoadedMetadata = () => {
  if (audioElement.value) {
    const audioDuration = audioElement.value.duration;
    if (isFinite(audioDuration) && audioDuration > 0) {
      duration.value = audioDuration;
    } else {
      duration.value = props.duration || 0;
    }
    isLoading.value = false;

    audioElement.value.volume = volume.value;
    audioElement.value.muted = isMuted.value;

    if (props.autoplay) {
      audioElement.value.play().catch((error) => {
        console.error("Autoplay prevented:", error);
      });
    }
  }
};

const onTimeUpdate = () => {
  if (audioElement.value) {
    currentTime.value = audioElement.value.currentTime;
  }
};

const onEnded = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  if (audioElement.value) {
    audioElement.value.currentTime = 0;
  }
  emit("ended");
};

watch(audioUrl, () => {
  if (audioElement.value) {
    hasError.value = false;
    audioElement.value.load();
  }
});

let lastErroredToken: string | null = null;

const onError = (event: Event) => {
  const currentToken = store.getters.sessionToken as string | null;
  if (currentToken && currentToken !== lastErroredToken && audioElement.value) {
    lastErroredToken = currentToken;
    audioElement.value.load();
    return;
  }
  console.error("Audio error:", event);
  hasError.value = true;
  isPlaying.value = false;
  isLoading.value = false;
  if (duration.value === 0 || !isFinite(duration.value)) {
    duration.value = props.duration || 0;
  }
  emit("error", new Error("Audio playback error"));
};

const toggleMute = () => {
  if (!audioElement.value) return;

  if (isMuted.value) {
    isMuted.value = false;
    volume.value = previousVolume.value;
    audioElement.value.muted = false;
    audioElement.value.volume = volume.value;
  } else {
    previousVolume.value = volume.value;
    isMuted.value = true;
    audioElement.value.muted = true;
  }
};

const onVolumeChange = (event: Event) => {
  if (!audioElement.value) return;
  const target = event.target as HTMLInputElement;
  const newVolume = parseFloat(target.value) / 100;

  volume.value = newVolume;
  audioElement.value.volume = newVolume;

  if (newVolume > 0 && isMuted.value) {
    isMuted.value = false;
    audioElement.value.muted = false;
  }

  if (newVolume === 0) {
    isMuted.value = true;
    audioElement.value.muted = true;
  }
};

watch(
  () => [props.recordingUrl, props.recordingSid],
  () => {
    if (audioElement.value) {
      audioElement.value.pause();
      isPlaying.value = false;
      currentTime.value = 0;
      duration.value = 0;
      hasError.value = false;
      isLoading.value = true;
    }
  }
);

watch(
  () => audioElement.value?.volume,
  (newVolume) => {
    if (audioElement.value && newVolume !== undefined && !isMuted.value) {
      volume.value = newVolume;
    }
  }
);

const handleClose = () => {
  if (audioElement.value) {
    audioElement.value.pause();
  }
  emit("close");
};

onUnmounted(() => {
  if (audioElement.value) {
    audioElement.value.pause();
    audioElement.value = null;
  }
});

return (_ctx: any,_cache: any) => {
  return (audioUrl.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(["audio-player", { 'audio-player--compact': __props.compact }]),
        onClick: _cache[6] || (_cache[6] = _withModifiers(() => {}, ["stop"]))
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("button", {
            class: "audio-player__play-btn",
            onClick: togglePlayback,
            disabled: isLoading.value || hasError.value,
            "aria-label": "Play/Pause"
          }, [
            (isLoading.value && !__props.hideLoadingState)
              ? (_openBlock(), _createBlock(_unref(Icon), {
                  key: 0,
                  icon: "mdi:loading",
                  width: "14",
                  height: "14",
                  class: "play-icon play-icon--spinning"
                }))
              : (hasError.value)
                ? (_openBlock(), _createBlock(_unref(Icon), {
                    key: 1,
                    icon: "mdi:alert-circle",
                    width: "14",
                    height: "14",
                    class: "play-icon"
                  }))
                : (_openBlock(), _createBlock(_unref(Icon), {
                    key: 2,
                    icon: isPlaying.value ? 'mdi:pause' : 'mdi:play',
                    width: "14",
                    height: "14",
                    class: "play-icon"
                  }, null, 8, ["icon"]))
          ], 8, _hoisted_2),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("div", {
                  class: "audio-player__progress-fill",
                  style: _normalizeStyle({ width: progress.value + '%' })
                }, null, 4)
              ]),
              _createElementVNode("input", {
                type: "range",
                class: "audio-player__progress",
                value: progress.value,
                min: "0",
                max: "100",
                step: "0.1",
                onInput: seekTo,
                onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
                disabled: isLoading.value || hasError.value || duration.value === 0,
                "aria-label": "Seek"
              }, null, 40, _hoisted_6)
            ]),
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("span", _hoisted_8, _toDisplayString(formattedCurrentTime.value), 1),
              _cache[7] || (_cache[7] = _createElementVNode("span", { class: "time-separator" }, "/", -1)),
              _createElementVNode("span", _hoisted_9, _toDisplayString(formattedAudioDuration.value), 1)
            ])
          ]),
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("input", {
              type: "range",
              class: "audio-player__volume-slider",
              value: volumePercent.value,
              min: "0",
              max: "100",
              step: "1",
              onInput: onVolumeChange,
              onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"])),
              "aria-label": "Volume"
            }, null, 40, _hoisted_11),
            _createElementVNode("button", {
              class: "audio-player__volume-btn",
              onClick: toggleMute,
              "aria-label": "Mute/Unmute"
            }, [
              _createVNode(_unref(Icon), {
                icon: volumeIcon.value,
                width: "16",
                height: "16",
                class: "volume-icon"
              }, null, 8, ["icon"])
            ])
          ]),
          _createElementVNode("audio", {
            ref_key: "audioElement",
            ref: audioElement,
            src: audioUrl.value,
            onLoadedmetadata: onLoadedMetadata,
            onTimeupdate: onTimeUpdate,
            onEnded: onEnded,
            onError: onError,
            onPlay: _cache[2] || (_cache[2] = ($event: any) => (isPlaying.value = true)),
            onPause: _cache[3] || (_cache[3] = ($event: any) => (isPlaying.value = false)),
            onLoadstart: _cache[4] || (_cache[4] = ($event: any) => (isLoading.value = true)),
            onCanplay: _cache[5] || (_cache[5] = ($event: any) => (isLoading.value = false)),
            preload: "metadata"
          }, null, 40, _hoisted_12),
          (__props.showCloseButton)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                class: "audio-player__close-btn",
                onClick: handleClose,
                "aria-label": "Close",
                type: "button"
              }, [
                _createVNode(_unref(Icon), {
                  icon: "mdi:close",
                  width: "14",
                  height: "14"
                })
              ]))
            : _createCommentVNode("", true)
        ]),
        (hasError.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
              _createVNode(_unref(Icon), {
                icon: "mdi:alert-circle",
                width: "14",
                height: "14"
              }),
              _cache[8] || (_cache[8] = _createElementVNode("span", null, "Unable to load audio", -1))
            ]))
          : _createCommentVNode("", true)
      ], 2))
    : _createCommentVNode("", true)
}
}

})